import type { Store, GraphEditorState } from '../types';
import { cloneGraphData } from './snapshots';

type SetState = (partial: Partial<Store> | ((state: Store) => Partial<Store>)) => void;
type GetState = () => Store;

export interface UndoEntry {
  description: string;
  graphData: GraphEditorState['graphData'];
  timestamp: number;
}

const MAX_STACK_SIZE = 50;

export function pushUndo(get: GetState, set: SetState, description: string): void {
  const entry: UndoEntry = {
    description,
    graphData: cloneGraphData(get().graphData),
    timestamp: Date.now(),
  };
  set((state) => ({
    history: {
      ...state.history,
      undoStack: [...state.history.undoStack, entry].slice(-MAX_STACK_SIZE),
      redoStack: [],
    },
  }));
}

export function createHistoryActions(set: SetState, get: GetState) {
  return {
    undo(): void {
      const stack = get().history.undoStack;
      if (stack.length === 0) return;

      const entry = stack[stack.length - 1];
      const currentSnapshot: UndoEntry = {
        description: entry.description,
        graphData: cloneGraphData(get().graphData),
        timestamp: Date.now(),
      };

      set((state) => ({
        graphData: entry.graphData,
        selection: { cellIds: [] },
        modals: { stack: [] },
        history: {
          ...state.history,
          undoStack: stack.slice(0, -1),
          redoStack: [...state.history.redoStack, currentSnapshot],
        },
      }));
    },

    redo(): void {
      const stack = get().history.redoStack;
      if (stack.length === 0) return;

      const entry = stack[stack.length - 1];
      const currentSnapshot: UndoEntry = {
        description: entry.description,
        graphData: cloneGraphData(get().graphData),
        timestamp: Date.now(),
      };

      set((state) => ({
        graphData: entry.graphData,
        selection: { cellIds: [] },
        modals: { stack: [] },
        history: {
          ...state.history,
          redoStack: stack.slice(0, -1),
          undoStack: [...state.history.undoStack, currentSnapshot],
        },
      }));
    },

    clearHistory(): void {
      set((state) => ({
        history: { ...state.history, undoStack: [], redoStack: [] },
      }));
    },
  };
}
